// src/pages/ScheduleRunsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import RunsHeader from '../components/runs/RunsHeader';
import RunsStats from '../components/runs/RunsStats';
import RunsFilters from '../components/runs/RunsFilters';
import RunsTable from '../components/runs/RunsTable';
import RunFormSlideOver from '../components/runs/RunFormSlideOver';
import DeleteRunDialog from '../components/runs/DeleteRunDialog';
import RunDetailsModal from '../components/runs/RunDetailsModal';

const API_BASE_URL = 'https://patrol-visit.patroll-management.nissiindia.com';

/**
 * Safely read a value from localStorage or sessionStorage.
 */
const getStoredValue = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key);

/**
 * Safely read and normalize the auth token.
 * - Handles JSON-stringified values (e.g. "\"eyJ...\"")
 * - Strips wrapping quotes
 * - Treats "null"/"undefined"/empty as missing
 */
const getAuthToken = () => {
  const raw = getStoredValue('authToken');
  if (!raw) return null;

  let token = raw;

  // If it was stored with JSON.stringify, parse it:
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      token = parsed;
    }
  } catch {
    // Not JSON, ignore
  }

  // Trim and strip accidental wrapping quotes
  token = token.trim().replace(/^"|"$/g, '');

  if (!token || token === 'null' || token === 'undefined') {
    return null;
  }

  return token;
};

/**
 * Safely read and normalize the auth role.
 * Same idea as token – can handle JSON-stringified "admin".
 */
const getAuthRole = () => {
  const raw = getStoredValue('authRole');
  if (!raw) return null;

  let role = raw;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      role = parsed;
    }
  } catch {
    // Not JSON, ignore
  }

  role = role.trim().replace(/^"|"$/g, '');

  if (!role || role === 'null' || role === 'undefined') {
    return null;
  }

  return role;
};

const ScheduleRunsPage = () => {
  const navigate = useNavigate();

  // Filters / query
  const [statusFilter, setStatusFilter] = useState('all'); // all | scheduled | in_progress | completed | cancelled
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Data
  const [runs, setRuns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRun, setEditingRun] = useState(null);
  const [deletingRun, setDeletingRun] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Derived stats
  const totalRuns = runs.length;
  const scheduledRuns = useMemo(
    () => runs.filter((r) => r.status === 'scheduled').length,
    [runs]
  );
  const inProgressRuns = useMemo(
    () => runs.filter((r) => r.status === 'in_progress').length,
    [runs]
  );
  const completedRuns = useMemo(
    () => runs.filter((r) => r.status === 'completed').length,
    [runs]
  );

  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  // Fetch list of runs
  const fetchRuns = async (opts = {}) => {
    try {
      setLoading(true);
      setError('');

      const currentPage = opts.page || page;

      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', limit);

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (dateFilter) {
        params.append('date', dateFilter);
      }

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/runs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to load runs');
      }

      const data = Array.isArray(json) ? json : json.data || [];

      // Client-side search by title / guard / date
      let filtered = data;
      if (search.trim()) {
        const s = search.trim().toLowerCase();
        filtered = data.filter((run) => {
          const title = (run.title || '').toLowerCase();
          const guardName = (
            (run.guard_first_name || '') +
            ' ' +
            (run.guard_last_name || '')
          )
            .trim()
            .toLowerCase();
          const dateStr = (run.run_date || '').toLowerCase();

          return (
            title.includes(s) ||
            guardName.includes(s) ||
            dateStr.includes(s)
          );
        });
      }

      setRuns(filtered);
      setPagination({
        page: currentPage,
        limit,
        total: data.length,
        totalPages: 1, // backend not paginating yet
      });
    } catch (err) {
      console.error('[fetchRuns]', err);
      setRuns([]);
      setError(err.message || 'Failed to load runs');
      toast.error(err.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch when filters/search change
  useEffect(() => {
    fetchRuns({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFilter, search]);

  // Fetch when page changes (for future server pagination)
  useEffect(() => {
    fetchRuns({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Create button
  const handleOpenCreate = () => {
    setEditingRun(null);
    setShowForm(true);
  };

  // Edit button
  const handleOpenEdit = (run) => {
    setEditingRun(run);
    setShowForm(true);
  };

  // Save (create / update)
  const handleSaveRun = async (formValues, { editingRun: runToEdit }) => {
    try {
      setSaving(true);
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      // Base payload
      const basePayload = {
        run_date: formValues.run_date,
        guard_id: formValues.guard_id,
        upload_mode: formValues.upload_mode,
        title: formValues.title?.trim() || null,
        notes: formValues.notes?.trim() || null,
        start_time: formValues.start_time || null,
        end_time: formValues.end_time || null,
      };

      let url = `${API_BASE_URL}/api/runs`;
      let method = 'POST';
      let payload = { ...basePayload, items: formValues.items || [] };

      // When editing, call PATCH /api/runs/:run_id (no items)
      if (runToEdit) {
        url = `${API_BASE_URL}/api/runs/${runToEdit.run_id}`;
        method = 'PATCH';
        payload = basePayload; // update only fields
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to save run');
      }

      await fetchRuns({ page: 1 });
      setShowForm(false);
      setEditingRun(null);

      toast.success(
        runToEdit ? 'Run updated successfully' : 'Run created successfully'
      );
    } catch (err) {
      console.error('[saveRun]', err);
      setError(err.message || 'Failed to save run');
      toast.error(err.message || 'Failed to save run');
    } finally {
      setSaving(false);
    }
  };

  // Cancel (soft delete via status)
  const confirmDelete = (run) => {
    setDeletingRun(run);
  };

  const handleDelete = async () => {
    if (!deletingRun) return;

    try {
      setDeleting(true);
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const url = `${API_BASE_URL}/api/runs/${deletingRun.run_id}/status`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'cancelled' }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to cancel run');
      }

      await fetchRuns({ page: 1 });
      setDeletingRun(null);
      toast.success('Run cancelled successfully');
    } catch (err) {
      console.error('[cancelRun]', err);
      setError(err.message || 'Failed to cancel run');
      toast.error(err.message || 'Failed to cancel run');
    } finally {
      setDeleting(false);
    }
  };

  // 🔹 Update status (from dropdown in RunsTable)
  const handleStatusChange = async (run, newStatus) => {
    try {
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const url = `${API_BASE_URL}/api/runs/${run.run_id}/status`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to update status');
      }

      // Option: refetch to keep filters in sync (run may disappear if it no longer matches status filter)
      await fetchRuns({ page: 1 });

      toast.success('Status updated successfully');
    } catch (err) {
      console.error('[updateStatus]', err);
      setError(err.message || 'Failed to update status');
      toast.error(err.message || 'Failed to update status');
    }
  };

  // View details (modal)
  const handleViewRun = async (run) => {
    try {
      setDetailsLoading(true);
      setSelectedRun(run); // optimistic – basic data first

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/runs/${run.run_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      console.log("response:", json);

      if (!res.ok) {
        throw new Error(json.message || 'Failed to load run details');
      }

      // json should be the full run with items
      setSelectedRun(json);
    } catch (err) {
      console.error('[viewRun]', err);
      toast.error(err.message || 'Failed to load run details');
      setSelectedRun(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <>
      {/* Toast container */}
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'text-sm',
        }}
      />

      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        {/* When form is NOT open → show dashboard style */}
        {!showForm && (
          <>
            <RunsHeader
              search={search}
              onSearchChange={setSearch}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              onAddClick={handleOpenCreate}
            />

            <RunsStats
              totalRuns={totalRuns}
              scheduledRuns={scheduledRuns}
              inProgressRuns={inProgressRuns}
              completedRuns={completedRuns}
            />

            <RunsFilters
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              visibleCount={runs.length}
              totalRuns={totalRuns}
            />

            <RunsTable
              runs={runs}
              loading={loading}
              error={error}
              pagination={pagination}
              onPageChange={setPage}
              onEdit={handleOpenEdit}
              onCancel={confirmDelete}
              onView={handleViewRun}
              onStatusChange={handleStatusChange} // 🔹 pass dropdown handler
            />

            <DeleteRunDialog
              run={deletingRun}
              deleting={deleting}
              onCancel={() => !deleting && setDeletingRun(null)}
              onConfirm={handleDelete}
            />

            <RunDetailsModal
              run={selectedRun}
              loading={detailsLoading}
              onClose={() => setSelectedRun(null)}
            />
          </>
        )}

        {/* When form IS open → show big form card with Back button */}
        <RunFormSlideOver
          isOpen={showForm}
          editingRun={editingRun}
          saving={saving}
          onClose={() => {
            if (!saving) {
              setShowForm(false);
              setEditingRun(null);
            }
          }}
          onSubmit={handleSaveRun}
        />
      </main>
    </>
  );
};

export default ScheduleRunsPage;
